import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { getMapboxToken } from '../../utils/functions';
import renewableProjects from '../../../static/data/data-renew.json';
import batteryIcon from '../../../static/images/renew-battery.svg';
import solarIcon from '../../../static/images/renew-solar.svg';
import windIcon from '../../../static/images/renew-wind.svg';
import pumpedHydroIcon from '../../../static/images/renew-pumped-hydro.svg';
import solarBatteryIcon from '../../../static/images/renew-solar-battery.svg';

const MAPBOX_ACCESS_TOKEN = getMapboxToken();
const SOURCE_ID = 'renewable-projects';
const ICON_LAYER_ID = 'renewable-project-type';
const MARKER_SIZE = 48;

const STATUS = {
  B2: { label: 'Operational', color: '#009E73' },
  D: { label: 'Approved', color: '#0072B2' },
  E: { label: 'Under construction', color: '#E69F00' },
  F: { label: 'Planning pipeline or under assessment', color: '#70757A' },
};

const TYPES = {
  battery: { id: 'renew-battery', label: 'Battery', src: batteryIcon },
  solar: { id: 'renew-solar', label: 'Solar', src: solarIcon },
  wind: { id: 'renew-wind', label: 'Wind', src: windIcon },
  'pumped hydro': {
    id: 'renew-pumped-hydro',
    label: 'Pumped hydro',
    src: pumpedHydroIcon,
  },
  'solar and battery': {
    id: 'renew-solar-battery',
    label: 'Solar and battery',
    src: solarBatteryIcon,
  },
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function createMarkerImage(icon, color) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  canvas.width = MARKER_SIZE;
  canvas.height = MARKER_SIZE;

  context.beginPath();
  context.arc(24, 24, 21, 0, Math.PI * 2);
  context.fillStyle = color;
  context.fill();
  context.lineWidth = 2;
  context.strokeStyle = '#FFFFFF';
  context.stroke();
  context.drawImage(icon, 9, 9, 30, 30);

  return context.getImageData(0, 0, MARKER_SIZE, MARKER_SIZE);
}

function createPopupContent(features) {
  const content = document.createElement('div');
  content.className = 'renew-popup';

  features.forEach((feature, index) => {
    const properties = feature.properties || {};
    const item = document.createElement('div');
    const name = document.createElement('strong');
    const meta = document.createElement('div');
    const statusDot = document.createElement('span');
    const details = document.createElement('span');
    const status = STATUS[properties.year];

    item.className = 'renew-popup__item';
    item.style.setProperty('--project-color', status?.color || '#70757A');
    name.textContent = properties.Name || 'Renewable energy project';
    meta.className = 'renew-popup__meta';
    statusDot.className = 'renew-popup__status-dot';
    statusDot.setAttribute('aria-hidden', 'true');
    details.className = 'renew-popup__details';
    details.textContent = TYPES[properties.type]?.label || properties.type;
    meta.append(statusDot, details);
    item.append(name, meta);

    if (properties.description?.trim()) {
      const description = document.createElement('p');
      description.className = 'map-popup__description';
      description.textContent = properties.description;
      item.append(description);
    }

    content.append(item);
    if (index < features.length - 1) {
      content.append(document.createElement('hr'));
    }
  });

  return content;
}

export default function MapRenew() {
  const mapContainer = useRef();

  useEffect(() => {
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/sbsonline/clxy6borh001q01oeeq147zxx',
      center: [151, -32.5],
      zoom: 7.5,
      minZoom: 5.5,
      maxZoom: 14,
      pitch: 0,
      bearing: 0,
      cooperativeGestures: true,
      maxBounds: [
        [149, -34],
        [152.3, -30.8],
      ],
    });

    map.setProjection('mercator');
    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      'top-left',
    );
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    map.scrollZoom.disable();
    let activePopup = null;

    map.on('load', async () => {
      const typeEntries = Object.values(TYPES);
      const images = await Promise.all(
        typeEntries.map(({ src }) => loadImage(src)),
      );

      typeEntries.forEach((type, index) => {
        Object.entries(STATUS).forEach(([status, item]) => {
          const markerId = `${type.id}-${status}`;
          if (!map.hasImage(markerId)) {
            map.addImage(
              markerId,
              createMarkerImage(images[index], item.color),
            );
          }
        });
      });

      const mapData = {
        ...renewableProjects,
        features: renewableProjects.features.map((feature) => ({
          ...feature,
          properties: {
            ...feature.properties,
            marker: `${TYPES[feature.properties.type].id}-${feature.properties.year}`,
          },
        })),
      };

      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: mapData,
      });

      map.addLayer({
        id: ICON_LAYER_ID,
        type: 'symbol',
        source: SOURCE_ID,
        layout: {
          'icon-image': ['get', 'marker'],
          'icon-size': ['interpolate', ['linear'], ['zoom'], 6, 0.7, 10, 1],
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-padding': 2,
        },
      });

      const bounds = new mapboxgl.LngLatBounds();
      renewableProjects.features.forEach(({ geometry }) => {
        if (geometry.type === 'Point') bounds.extend(geometry.coordinates);
      });
      map.fitBounds(bounds, {
        padding: window.innerWidth > 600 ? 70 : 40,
        duration: 0,
        maxZoom: 9,
      });
    });

    map.on('click', ICON_LAYER_ID, (event) => {
      if (!event.features || !event.features.length) return;

      if (activePopup) activePopup.remove();
      const clickedFeature = event.features[0];

      activePopup = new mapboxgl.Popup({ offset: 22, closeButton: true })
        .setLngLat(event.lngLat)
        .setDOMContent(createPopupContent([clickedFeature]))
        .addTo(map);
    });

    map.on('mouseenter', ICON_LAYER_ID, () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', ICON_LAYER_ID, () => {
      map.getCanvas().style.cursor = '';
    });

    return () => {
      if (activePopup) activePopup.remove();
      map.remove();
    };
  }, []);

  return (
    <div>
      <div className="renew-map-legends">
        <div className="renew-map-legend" aria-label="Project status legend">
          {Object.entries(STATUS).map(([status, item]) => (
            <div className="renew-map-legend__item" key={status}>
              <span
                className="renew-map-legend__status"
                style={{ backgroundColor: item.color }}
                aria-hidden="true"
              />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
        <div className="renew-map-legend" aria-label="Project type legend">
          {Object.entries(TYPES).map(([type, item]) => (
            <div className="renew-map-legend__item" key={type}>
              <img src={item.src} alt="" />
              <span>{item.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div id="map-wrap">
        <div
          ref={mapContainer}
          className="mapContainer"
          aria-label="Map of renewable energy projects by type and status"
        />
      </div>
    </div>
  );
}
